// This cache holds one branding image only. MFA decisions and challenges are never cached.
export interface CachedMfaLogo { hash: string; logo: string; }
const CACHE_NAME = 'minmfa-branding-v1';
const CACHE_KEY = '/__minmfa_branding__/current';

async function verified(value: CachedMfaLogo): Promise<boolean> {
  if (!value || !/^[a-f0-9]{64}$/.test(value.hash) || typeof value.logo !== 'string' ||
      !value.logo.startsWith('data:image/') || value.logo.length > 2 * 1024 * 1024) return false;
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value.logo));
  const hash = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  return hash === value.hash;
}

export async function loadMfaLogo(): Promise<CachedMfaLogo | null> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(CACHE_KEY);
    const value = response ? await response.json() : null;
    return await verified(value) ? value : null;
  } catch {
    return null; // Storage/crypto unavailable: normal full-image delivery.
  }
}

export async function saveMfaLogo(value: CachedMfaLogo): Promise<void> {
  try {
    if (!await verified(value)) return;
    const cache = await caches.open(CACHE_NAME);
    await cache.put(CACHE_KEY, new Response(JSON.stringify(value), {
      headers: { 'Content-Type': 'application/json' },
    }));
  } catch {
    // Optional branding storage must never block authentication.
  }
}
